import React, { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Modal,
} from '@mui/material';
import useDeleteUser from '../../hooks/queries/useDeleteUser';
import { useAppSelector } from '../../redux/hooks';
import { useNavigate } from 'react-router-dom';
import { useForgotPassword } from '../../hooks/queries/useForgotPassword';
import {
  ArrowLeftIcon,
  CancelIcon,
  LockIcon,
  SettingsIcon,
  TestIcon,
} from '../../components/shared/CustomIcons';
import MESSAGES from '../../constants/message';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 342,
  bgcolor: 'common.white',
  textAlign: 'center',
  px: 2.75,
  py: 5,
  borderRadius: 8,
  boxShadow: '4px 4px 16px 8px #10182808',
};

const SettingsPage: React.FC = () => {
  const { mutate, status, isError, error } = useDeleteUser();
  const { sendForgotPasswordRequest, status: resetPasswordStatus } =
    useForgotPassword(true);
  const navigate = useNavigate();
  const [isOpen, setModalOpen] = useState(false);
  const [isWarning, setWarning] = useState(Boolean(isError));
  console.log(isError, error);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const userID = useAppSelector((state) => state.auth.currentUser?.id);

  const email = useAppSelector((state) => state.auth.currentUser?.email);
  const handleBackClick = () => {
    // Handle back button action
    history.back();
  };

  const handleDeleteAccount = () => {
    // Handle delete account action
    mutate(userID);
  };

  const handleResetPassword = () => {
    if (email) sendForgotPasswordRequest(email);
  };
  useEffect(() => {
    if (status === 'success') {
      localStorage.clear();
      navigate('/');
      setModalOpen(false);
    }
  }, [navigate, status]);

  useEffect(() => {
    if (resetPasswordStatus === 'success') {
      setIsResetModalOpen(true);
    }
  }, [resetPasswordStatus]);

  useEffect(() => {
    setModalOpen(false);
    setWarning(isError);
  }, [isError]);

  return (
    <Box sx={{ bgcolor: 'grey.50', px: 2, py: 8 }} className="flex-1">
      <Box sx={{ px: 1, mb: 2 }} className="d-flex align-items-center">
        <IconButton aria-label="back" sx={{ p: 0 }} onClick={handleBackClick}>
          <ArrowLeftIcon sx={{ fontSize: 40, color: 'text.primary' }} />
        </IconButton>
        <Box
          sx={{ gap: 1.5, ml: -5 }}
          className="d-flex justify-content-center align-items-center flex-1"
        >
          <SettingsIcon
            sx={{ color: 'tertiary.main', width: 24, height: 24 }}
          />
          <Typography variant="h6" sx={{ color: 'grey.900' }}>
            Settings
          </Typography>
        </Box>
      </Box>
      <List component="div" sx={{ p: 0 }}>
        <ListItemButton
          onClick={() => navigate('/profile/terms-and-conditions')}
          sx={{ color: 'grey.900', gap: 2, px: 1, py: 2, borderRadius: 4 }}
        >
          <ListItemIcon sx={{ minWidth: 24 }}>
            <TestIcon sx={{ color: 'grey.900', width: 24, height: 24 }} />
          </ListItemIcon>
          <ListItemText
            primary="Terms and conditions"
            sx={{ m: 0 }}
            disableTypography
          />
        </ListItemButton>
        <ListItemButton
          onClick={handleResetPassword}
          sx={{ color: 'grey.900', gap: 2, px: 1, py: 2, borderRadius: 4 }}
        >
          <ListItemIcon sx={{ minWidth: 24 }}>
            <LockIcon sx={{ color: 'grey.900', width: 24, height: 24 }} />
          </ListItemIcon>
          <ListItemText
            primary="Reset password"
            sx={{ m: 0 }}
            disableTypography
          />
        </ListItemButton>
        <Divider sx={{ borderColor: 'solitude.main', m: 1 }} />
        <ListItemButton
          onClick={() => setModalOpen(true)}
          sx={{ color: 'error.main', gap: 2, px: 1, py: 2, borderRadius: 4 }}
        >
          <ListItemIcon sx={{ minWidth: 24 }}>
            <CancelIcon sx={{ color: 'error.main', width: 24, height: 24 }} />
          </ListItemIcon>
          <ListItemText
            primary="Delete my account"
            sx={{ m: 0 }}
            disableTypography
          />
        </ListItemButton>
      </List>
      <Modal
        open={isOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{ color: 'tertiary.main', mb: 1.5 }}
          >
            Are you sure you want to delete your account?
          </Typography>
          <Typography id="modal-modal-description" sx={{ mb: 4 }}>
            By deleting your account, you won’t be able to revert this action.
            All your data will be lost.
          </Typography>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleDeleteAccount}
            sx={{ mb: 2 }}
          >
            Delete my account
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setModalOpen(false)}
          >
            keep my account
          </Button>
        </Box>
      </Modal>

      {/* Future appointment warning */}
      <Modal
        open={isWarning}
        onClose={() => {
          setWarning(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{ color: 'tertiary.main', mb: 1.5 }}
          >
            {MESSAGES.USER.DELETE.FAIL.FUTURE_APPOINTMENT.TITLE}
          </Typography>
          <Typography id="modal-modal-description" sx={{ mb: 4 }}>
            {MESSAGES.USER.DELETE.FAIL.FUTURE_APPOINTMENT.DESCRIPTION}
          </Typography>

          <Button
            variant="contained"
            fullWidth
            onClick={() => setWarning(false)}
          >
            {MESSAGES.USER.DELETE.FAIL.FUTURE_APPOINTMENT.BUTTON_NAME}
          </Button>
        </Box>
      </Modal>

      <Modal
        open={isResetModalOpen}
        onClose={() => {
          setIsResetModalOpen(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{ color: 'tertiary.main', mb: 1.5 }}
          >
            We’ve sent you a password reset email
          </Typography>
          <Typography id="modal-modal-description" sx={{ mb: 4 }}>
            Please check your inbox and follow the instructions provided.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setIsResetModalOpen(false)}
          >
            I understand
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default SettingsPage;
